import { ShoppingList, ShoppingListItem } from "../types";

/**
 * Formats a shopping list as text with an AI prompt for categorization
 */
export function formatShoppingListForAI(shoppingList: ShoppingList): string {
  const uncheckedItems = shoppingList.items.filter((item) => !item.checked);

  if (uncheckedItems.length === 0) {
    return "";
  }

  const itemsText = uncheckedItems.map((item) => item.text).join("\n");

  return `Please organize this shopping list by category (such as Produce, Dairy, Meat/Protein, Bakery, Pantry/Dry Goods, Frozen, Beverages, etc.). Return the list in this exact format:

## [Category Name]
- item 1
- item 2

## [Category Name]
- item 3
- item 4

Shopping List:
${itemsText}`;
}

/**
 * Parses AI-categorized text back into shopping list items with categories
 */
export function parseCategorizedTextToItems(
  categorizedText: string,
  originalItems: ShoppingListItem[]
): ShoppingListItem[] {
  const lines = categorizedText.split("\n").filter((line) => line.trim());
  const updatedItems: ShoppingListItem[] = [];
  let currentCategory = "";

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check if it's a category header (starts with ##)
    if (trimmedLine.startsWith("##")) {
      currentCategory = trimmedLine.replace(/^##\s*/, "").trim();
      continue;
    }

    // Check if it's an item (starts with - or *)
    if (trimmedLine.startsWith("-") || trimmedLine.startsWith("*")) {
      const itemText = trimmedLine.replace(/^[-*]\s*/, "").trim();

      // Find the original item by matching text (case-insensitive, fuzzy)
      const originalItem = originalItems.find((item) =>
        item.text.toLowerCase().includes(itemText.toLowerCase()) ||
        itemText.toLowerCase().includes(item.text.toLowerCase())
      );

      if (originalItem) {
        updatedItems.push({
          ...originalItem,
          category: currentCategory || undefined,
        });
      } else {
        // If no match found, create a new item
        updatedItems.push({
          id: `new-${Date.now()}-${Math.random()}`,
          text: itemText,
          checked: false,
          category: currentCategory || undefined,
        });
      }
    }
  }

  // Add any items that weren't matched (keep them uncategorized)
  const matchedIds = new Set(updatedItems.map((item) => item.id));
  const unmatchedItems = originalItems.filter(
    (item) => !matchedIds.has(item.id) && !item.checked
  );
  updatedItems.push(...unmatchedItems);

  // Keep checked items at the end
  const checkedItems = originalItems.filter((item) => item.checked);
  updatedItems.push(...checkedItems);

  return updatedItems;
}

/**
 * Groups shopping list items by category
 */
export function groupItemsByCategory(
  items: ShoppingListItem[]
): Map<string, ShoppingListItem[]> {
  const grouped = new Map<string, ShoppingListItem[]>();

  items.forEach((item) => {
    const category = item.category || "Uncategorized";
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)!.push(item);
  });

  // Sort categories: put Uncategorized last
  const sortedMap = new Map<string, ShoppingListItem[]>();
  const sortedCategories = Array.from(grouped.keys()).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });

  sortedCategories.forEach((category) => {
    sortedMap.set(category, grouped.get(category)!);
  });

  return sortedMap;
}
